import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
  type ActionFunctionArgs,
  type MetaFunction,
  json,
  redirect,
} from '@remix-run/node'
import { Form } from '@remix-run/react'
import { useState } from 'react'
import { z } from 'zod'
import { ContactService } from '#app/entities/contact/service.server.js'
import { TasacionService } from '#app/entities/tasacion/service.server.js'
import VendeTuCocheEmail from '#app/features/email/vendeTuCocheEmail.tsx'
import { getMetaTags } from '#app/shared/lib/meta-tags.ts'
import {
  phoneSchema,
  postalCodeSchema,
} from '#app/shared/lib/user-validation.ts'
import { Spacer } from '#app/shared/ui/data-display/Spacer.tsx'
import { redirectWithToast } from '#app/shared/ui/feedback/toast/toast.server.js'
import { Field, PhoneField, TextareaField } from '#app/shared/ui/fields.tsx'
import { Button } from '#app/shared/ui/inputs/button/Button.tsx'
import { CheckboxTermsAndPrivacy } from '#app/shared/ui/inputs/CheckboxTermsAndPrivacy.js'
import Header from '#app/shared/ui/layout/Header.tsx'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'
import {
  Breadcrumb,
  breadcrumbHome,
} from '#app/shared/ui/navigation/Breadcrumb.tsx'
import { ContactTypeEnum } from '../../../../types/enums'

const ContactoFormSchema = z.object({
  name: z.string().min(1, 'Por favor, introduce tu nombre'),
  postalCode: postalCodeSchema,
  email: z.string().email('Por favor, introduce un email válido'),
  phoneNumber: phoneSchema,
  fullPhone: z.string(),
  message: z
    .string()
    .min(10, 'Por favor, introduce un mensaje de al menos 10 caracteres'),
})

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const submission = parseWithZod(formData, {
    schema: ContactoFormSchema,
  })

  if (submission.status !== 'success') {
    return json(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  const { name, postalCode, email, fullPhone, message } = submission.value

  const contact = await ContactService.create({
    name,
    postalCode,
    email,
    phoneNumber: fullPhone,
    message,
    type: ContactTypeEnum.APPRAISAL,
  })

  if (contact.success) {
    const tasation = await TasacionService.createEmptyTasation(
      contact.contact?.id,
    )
    if (contact.success && tasation.success) {
      try {
        await VendeTuCocheEmail({
          name,
          postalCode,
          email,
          fullPhone,
          message,
        })
      } catch (error) {
        console.error('Error sending VendeTuCocheEmail:', error)
      }
      return redirect('/contacto-result')
    } else {
      await ContactService.delete(contact.contact?.id)
      return redirectWithToast('./', {
        title: 'Mensaje no enviado',
        description: 'Ha ocurrido un error al enviar la tasación',
        type: 'error',
      })
    }
  }

  return redirectWithToast('./', {
    title: 'Mensaje no enviado',
    description: 'Ha ocurrido un error al enviar el mensaje',
    type: 'error',
  })
}

export const meta: MetaFunction = ({ matches }) => {
  return getMetaTags({
    matches,
    title: 'Vende tu Coche | Gama Óptima',
    description:
      'Vende tu coche o úsalo como parte del pago en Gama Óptima. Te garantizamos la mejor tasación del mercado. Proceso rápido y sin complicaciones en Barcelona.',
    url: 'https://gamaoptima.com/vende-tu-coche',
  })
}

export default function VendeTuCoche() {
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)

  const [form, fields] = useForm({
    id: 'login-form',
    constraint: getZodConstraint(ContactoFormSchema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ContactoFormSchema })
    },
    shouldRevalidate: 'onBlur',
  })

  return (
    <>
      <Header isSticky={true} showBanner={true} />
      <section className={`mx-auto w-full max-w-[1150px] bg-neutral-25`}>
        <MainContainer className="max-w-[800px]">
          <>
            <Breadcrumb items={[breadcrumbHome, { label: 'Vende tu coche' }]} />
            <div
              className={`
                flex w-full flex-col items-start justify-items-start px-5

                lg:pl-0
              `}
            >
              <Spacer size="20px" />
              {/* Form */}
              <div>
                <h3 className={`w-full px-0 text-h3`}>
                  En Gama Óptima recibimos tu coche como parte del pago,
                  garantizando la mejor tasación.
                </h3>
                <Spacer size="16px" />
                <img
                  src="/img/vende-tu-coche-imagen.png"
                  alt="Vende tu coche"
                  className="w-full object-contain"
                />
                <Spacer size="16px" />
                <p className={`text-neutral-500`}>
                  Cuéntanos todo sobre el coche que quieres usar como parte del
                  pago, y nos pondremos en contacto contigo a la brevedad para
                  confirmar la tasación.
                </p>
              </div>
              <Spacer size="36px" />
              {/* Form */}
              <Form
                className={`w-full space-y-7`}
                method="post"
                {...getFormProps(form)}
              >
                <TextareaField
                  labelProps={{
                    htmlFor: fields.message.id,
                    children: 'Tu mensaje',
                  }}
                  textareaProps={{
                    ...getInputProps(fields.message, { type: 'text' }),
                    autoComplete: 'message',
                    placeholder:
                      'Cuéntanos los detalles del coche que estas buscando',
                    className: 'mt-2 rounded-lg',
                    rows: 3,
                  }}
                  textareaClassName="rounded-lg placeholder:text-neutral-500"
                  errors={fields.message.errors}
                />
                <Field
                  labelProps={{
                    htmlFor: fields.name.id,
                    children: 'Nombre',
                  }}
                  inputProps={{
                    ...getInputProps(fields.name, { type: 'text' }),
                    autoComplete: 'name',
                    placeholder: 'Escribe aquí tu nombre',
                    className: 'mt-2 rounded-lg',
                  }}
                  inputClassName="rounded-lg placeholder:text-neutral-500"
                  errors={fields.name.errors}
                />
                <Field
                  labelProps={{
                    htmlFor: fields.postalCode.id,
                    children: 'Código postal',
                  }}
                  inputProps={{
                    ...getInputProps(fields.postalCode, { type: 'text' }),
                    autoComplete: 'postal code',
                    placeholder: 'Escribe aquí tu código postal',
                    className: 'mt-2 rounded-lg',
                  }}
                  inputClassName="rounded-lg placeholder:text-neutral-500"
                  errors={fields.postalCode.errors}
                />
                <Field
                  labelProps={{
                    htmlFor: fields.email.id,
                    children: 'Email',
                  }}
                  inputProps={{
                    ...getInputProps(fields.email, { type: 'email' }),
                    autoComplete: 'email',
                    className: 'mt-2 rounded-lg',
                    placeholder: 'Escribe aquí tu email',
                  }}
                  inputClassName="rounded-lg placeholder:text-neutral-500"
                  errors={fields.email.errors}
                />
                <PhoneField
                  labelProps={{
                    htmlFor: fields.phoneNumber.id,
                    children: 'Teléfono',
                  }}
                  inputProps={{
                    ...getInputProps(fields.phoneNumber, { type: 'number' }),
                    autoComplete: 'phone',
                    placeholder: 'Escribe aquí tu teléfono',
                    className: 'mt-2',
                  }}
                  fullPhone={fields.fullPhone.name}
                  errors={fields.phoneNumber.errors}
                />
                <CheckboxTermsAndPrivacy
                  isTermsAccepted={isTermsAccepted}
                  setIsTermsAccepted={setIsTermsAccepted}
                />
                <Button
                  type="submit"
                  className={`
                    px-6 py-5 text-base-bold text-white

                    disabled:opacity-20
                  `}
                  disabled={!isTermsAccepted}
                >
                  Enviar
                </Button>
              </Form>
            </div>
          </>
        </MainContainer>
        <Spacer size="48px" />
      </section>
    </>
  )
}
